import { z } from "zod";
// Une valeur, pas un type : les regles du texte n'importent de ce fichier que des types, et
// aucun des deux modules n'attend l'autre au chargement.
import { estEcrite } from "./histoires-texte";

/**
 * Contrat du module Histoires de la console (spec du 26/09/2026, amendee le 27/09 ; ADR 0012).
 *
 * Ce fichier vit sous `src/app/` parce que `src/index.ts` appartient au template : backend et
 * frontend le lisent par leur pont `src/app/contract.ts`. Le paquet Python `histoire` parle le
 * meme JSON. Les enumerations disent exactement ce que disent les contraintes CHECK de
 * `migrations/1002_histoires.sql` : un test du backend les compare. Un nom porte « Histoire »
 * quand le coloriage a deja le meme : le pont reexporte tous les modules d'un bloc.
 *
 * Les cles JSON sont en `snake_case`, comme celles du coloriage. Les regles du texte (compter
 * les mots, trouver les mots interdits, dire si une double est ecrite) vivent a cote, dans
 * `histoires-texte.ts`, pour que l'ecran et le serveur disent exactement la meme chose.
 */

const identifiant = z.number().int().positive();
const compte = z.number().int().nonnegative();
/** Le numero de version du texte d'une double : augmente a chaque ecriture, 409 sinon. */
const versionTexte = z.number().int().nonnegative();
/** Une date rendue par la console, en ISO 8601. */
const instant = z.string();

export const etatLivreSchema = z.enum(["ouvert", "envoye", "en_ligne", "clos"]);
export type EtatLivre = z.infer<typeof etatLivreSchema>;

export const typeDoubleSchema = z.enum([
  "couverture",
  "titre",
  "liminaire",
  "histoire",
  "note",
  "blanche",
]);
export type TypeDouble = z.infer<typeof typeDoubleSchema>;

/** Qui a ecrit le texte d'une double : la declaration IA du livre en decoule (spec, section 2). */
export const provenanceSchema = z.enum(["ecrit", "assiste", "genere"]);
export type Provenance = z.infer<typeof provenanceSchema>;

export const origineJournalSchema = z.enum(["nicolas", "claude"]);
export type OrigineJournal = z.infer<typeof origineJournalSchema>;
export const modeJournalSchema = z.enum([
  "saisie",
  "import",
  "propose",
  "rewrite",
  "restauration",
  "effacement",
]);
export type ModeJournal = z.infer<typeof modeJournalSchema>;
export const gabaritSchema = z.enum([
  "bandeau_bas",
  "bandeau_haut",
  "texte_gauche",
  "texte_droite",
  "image_gauche",
  "image_droite",
  "texte_seul",
]);
export type Gabarit = z.infer<typeof gabaritSchema>;
export const roleVersionSchema = z.enum(["planche", "illustration"]);
export const fournisseurSchema = z.enum(["comfy", "gemini"]);
export const typeDemandeHistoireSchema = z.enum(["planche", "illustrer", "monter"]);
export const etatDemandeHistoireSchema = z.enum([
  "en_attente",
  "en_cours",
  "faite",
  "echouee",
  "annulee",
]);
export const etatComparaisonHistoireSchema = z.enum(["ouverte", "close"]);
export const etatComfyHistoireSchema = z.enum(["ok", "injoignable", "occupe"]);
export const etatGeminiSchema = z.enum(["configure", "absent"]);

/**
 * Ce que le livre doit declarer a KDP pour son texte (spec, section 2) : `genere` des qu'une
 * double `histoire` ou `note` l'est, `assiste` si aucune ne l'est et qu'au moins une est
 * `assiste`, sinon `aucune`.
 */
export const declarationTexteSchema = z.enum(["aucune", "assiste", "genere"]);
export type DeclarationTexte = z.infer<typeof declarationTexteSchema>;

/** Reponse de `POST /api/histoires/machine/ping` : le jeton du PC est accepte. */
export const pingHistoiresSchema = z.object({
  module: z.literal("histoires"),
  ok: z.literal(true),
});
export type PingHistoires = z.infer<typeof pingHistoiresSchema>;

// ---------------------------------------------------------------------------------------
// Le livre, son brief, ses personnages.

/** La cle d'un livre : le nom de son dossier sur le PC, meme regle que la base. */
export const cleLivreSchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9-]{0,119}$/, "minuscules, chiffres et tirets")
  .refine((cle) => !/^(con|prn|aux|nul|com[0-9]|lpt[0-9])$/.test(cle), "nom reserve de Windows");

/**
 * Un mot ou une expression que le texte ne doit pas contenir, et pourquoi. Un mot sans
 * caractere ecrit (des blancs ou des invisibles seulement) ne designerait rien : refuse.
 */
export const motInterditSchema = z.object({
  mot: z
    .string()
    .trim()
    .min(1)
    .max(80)
    .refine(estEcrite, "un mot interdit a au moins un caractere ecrit"),
  raison: z.string().trim().max(300).nullable().default(null),
});
export type MotInterdit = z.infer<typeof motInterditSchema>;

/** La fourchette de mots d'une double `histoire`. */
export const fourchetteSchema = z
  .object({ min: z.number().int().min(0).max(2000), max: z.number().int().min(1).max(2000) })
  .refine((f) => f.min <= f.max, "le minimum depasse le maximum");
export type Fourchette = z.infer<typeof fourchetteSchema>;

/**
 * Le brief d'un livre. Chaque champ a sa valeur par defaut : le brief d'un livre neuf, et un
 * brief range vide en base (`{}`), se lisent comme `briefSchema.parse({})`.
 */
export const briefSchema = z.object({
  sujet: z.string().max(2000).default(""),
  ages: z.string().max(40).default(""),
  fil: z.string().max(2000).default(""),
  mots_interdits: z.array(motInterditSchema).max(100).default([]),
  phrases_attendues: z.array(z.string().trim().min(1).max(300)).max(30).default([]),
  mots_par_double: fourchetteSchema.nullable().default(null),
  /** Le reste du grill : la voix, le monde, la longueur. Libre. */
  notes: z.string().max(4000).default(""),
});
export type Brief = z.infer<typeof briefSchema>;

export const personnageSchema = z.object({
  id: identifiant,
  rang: identifiant,
  nom: z.string(),
  espece: z.string().nullable(),
  /** En anglais : ils partent dans les prompts. */
  description: z.string().nullable(),
  signes: z.string().nullable(),
  notes: z.string().nullable(),
});
export type Personnage = z.infer<typeof personnageSchema>;

export const personnageEcritSchema = z.object({
  nom: z.string().trim().min(1).max(80),
  espece: z.string().trim().max(80).nullable().default(null),
  description: z.string().max(2000).nullable().default(null),
  signes: z.string().max(1000).nullable().default(null),
  notes: z.string().max(2000).nullable().default(null),
});
export type PersonnageEcrit = z.infer<typeof personnageEcritSchema>;

export const livreSchema = z.object({
  id: identifiant,
  cle: z.string(),
  titre: z.string(),
  sous_titre: z.string().nullable(),
  auteur: z.string().nullable(),
  langue: z.string(),
  etat: etatLivreSchema,
  brief: briefSchema,
  /** La phrase de style commune a toutes les images, en anglais. */
  style: z.string().nullable(),
  maj_le: instant,
});
export type Livre = z.infer<typeof livreSchema>;

// ---------------------------------------------------------------------------------------
// L'accueil.

/**
 * Un livre dans la liste de l'accueil. `doubles` compte ses doubles de type `histoire`,
 * `ecrites` celles dont le texte n'est pas vide : c'est la mesure H2 (spec, amendement 1).
 */
export const livreResumeSchema = z.object({
  id: identifiant,
  cle: z.string(),
  titre: z.string(),
  etat: etatLivreSchema,
  doubles: compte,
  ecrites: compte,
  maj_le: instant,
});
export type LivreResume = z.infer<typeof livreResumeSchema>;

export const listeLivresSchema = z.object({ livres: z.array(livreResumeSchema) });
export type ListeLivres = z.infer<typeof listeLivresSchema>;

// ---------------------------------------------------------------------------------------
// Le chemin de fer et une double.

/** Une alerte d'une double, calculee par les regles de `histoires-texte.ts`. */
export const alerteSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("mot_interdit"), mots: z.array(z.string()).min(1) }),
  z.object({ type: z.literal("hors_fourchette"), mots: compte, min: compte, max: compte }),
]);
export type Alerte = z.infer<typeof alerteSchema>;

/** Une carte du chemin de fer. */
export const doubleResumeSchema = z.object({
  id: identifiant,
  rang: compte,
  type: typeDoubleSchema,
  pages: z.number().int().min(0).max(2),
  titre: z.string().nullable(),
  intention: z.string().nullable(),
  /** Le debut du texte (160 caracteres au plus), ou null s'il n'y en a pas. */
  extrait: z.string().nullable(),
  mots: compte,
  ecrite: z.boolean(),
  provenance: provenanceSchema.nullable(),
  alertes: z.array(alerteSchema),
  gabarit: gabaritSchema.nullable(),
});
export type DoubleResume = z.infer<typeof doubleResumeSchema>;

export const livreDetailSchema = z.object({
  livre: livreSchema,
  doubles: z.array(doubleResumeSchema),
  personnages: z.array(personnageSchema),
  mesure: z.object({ doubles: compte, ecrites: compte }),
  declaration_texte: declarationTexteSchema,
});
export type LivreDetail = z.infer<typeof livreDetailSchema>;

export const doubleSchema = z.object({
  id: identifiant,
  rang: compte,
  type: typeDoubleSchema,
  pages: z.number().int().min(0).max(2),
  titre: z.string().nullable(),
  intention: z.string().nullable(),
  texte: z.string().nullable(),
  provenance: provenanceSchema.nullable(),
  texte_version: versionTexte,
  gabarit: gabaritSchema.nullable(),
  /** En anglais : la place du texte decrite comme un morceau du decor. */
  composition: z.string().nullable(),
  personnages: z.array(identifiant),
  figurants: z.string().nullable(),
  notes: z.string().nullable(),
  maj_le: instant,
});
export type Double = z.infer<typeof doubleSchema>;

export const ligneJournalSchema = z.object({
  id: identifiant,
  origine: origineJournalSchema,
  mode: modeJournalSchema,
  texte_avant: z.string().nullable(),
  texte_apres: z.string().nullable(),
  provenance_avant: provenanceSchema.nullable(),
  provenance_apres: provenanceSchema.nullable(),
  cree_le: instant,
  maj_le: instant,
});
export type LigneJournal = z.infer<typeof ligneJournalSchema>;

export const doubleVoisineSchema = z.object({
  id: identifiant,
  rang: compte,
  type: typeDoubleSchema,
  titre: z.string().nullable(),
});

export const doubleDetailSchema = z.object({
  livre: z.object({
    id: identifiant,
    cle: z.string(),
    titre: z.string(),
    brief: briefSchema,
    declaration_texte: declarationTexteSchema,
  }),
  double: doubleSchema,
  /** Le plus recent d'abord, cinquante lignes au plus. */
  journal: z.array(ligneJournalSchema),
  precedente: doubleVoisineSchema.nullable(),
  suivante: doubleVoisineSchema.nullable(),
  personnages_du_livre: z.array(personnageSchema),
});
export type DoubleDetail = z.infer<typeof doubleDetailSchema>;

// ---------------------------------------------------------------------------------------
// Les ecritures de Nicolas (session, liste blanche, `Origin`).

/** Le texte d'une double, en unites UTF-16 (la longueur de JavaScript et de Zod). */
export const TEXTE_MAX = 20_000;
/**
 * Un manuscrit colle, en unites UTF-16 : un album entier en fait environ 30 000. La route qui
 * le lit a son propre plafond de corps (`PLAFOND_MANUSCRIT`), assez haut pour qu'un manuscrit
 * sous cette borne ne prenne jamais 413.
 */
export const MANUSCRIT_MAX = 100_000;
/**
 * Les notes d'une double, en unites UTF-16 : celles que Nicolas ecrit, et les lignes
 * `[Image : ...]` qu'un manuscrit colle y ajoute.
 */
export const NOTES_MAX = 4000;

export const creerLivreSchema = z.object({
  cle: cleLivreSchema,
  titre: z.string().trim().min(1).max(200),
});
export const livreCreeSchema = z.object({
  livre: z.object({ id: identifiant, cle: z.string() }),
});

/** Le texte d'une double, avec la version qu'il remplace. */
export const ecrireTexteSchema = z.object({
  texte: z.string().max(TEXTE_MAX),
  version: versionTexte,
});
/** « Repartir de zero » et « Restaurer » : la version qu'on remplace, rien d'autre. */
export const versionSeuleSchema = z.object({ version: versionTexte });

/** Les metadonnees d'une double, en partie : un champ absent ne change pas. */
export const modifierDoubleSchema = z
  .object({
    titre: z.string().trim().max(120).nullable(),
    intention: z.string().max(2000).nullable(),
    gabarit: gabaritSchema.nullable(),
    composition: z.string().max(4000).nullable(),
    personnages: z.array(identifiant).max(20),
    figurants: z.string().max(2000).nullable(),
    notes: z.string().max(NOTES_MAX).nullable(),
  })
  .partial()
  .refine((m) => Object.keys(m).length > 0, "au moins un champ");

export const briefEcritSchema = z.object({
  brief: briefSchema,
  style: z.string().max(1000).nullable(),
});

/**
 * « Coller un manuscrit » : un texte decoupe par ses titres `## N.` ; `essai` rend l'apercu sans
 * rien ecrire. Seules les doubles `histoire` au texte `NULL` sont remplies (spec, amendement 4).
 */
export const manuscritSchema = z.object({
  texte: z.string().min(1).max(MANUSCRIT_MAX),
  essai: z.boolean(),
});

// Les reponses aux ecritures.

export const texteEcritSchema = z.object({
  texte_version: versionTexte,
  provenance: provenanceSchema.nullable(),
  maj_le: instant,
});
export type TexteEcrit = z.infer<typeof texteEcritSchema>;

/**
 * Le 409 d'une ecriture de texte : l'erreur au format du template, et le texte courant, pour
 * que l'ecran propose de recharger et dise qui a ecrit entre-temps.
 */
export const conflitTexteSchema = z.object({
  error: z.object({
    code: z.literal("texte_modifie"),
    message: z.string(),
    requestId: z.string().optional(),
  }),
  courant: z.object({
    texte: z.string().nullable(),
    provenance: provenanceSchema.nullable(),
    texte_version: versionTexte,
    derniere_origine: origineJournalSchema.nullable(),
  }),
});
export type ConflitTexte = z.infer<typeof conflitTexteSchema>;

export const apercuManuscritSchema = z.object({
  /** Les sections qui rempliront une double vide. */
  remplies: z.array(
    z.object({
      numero: identifiant,
      double_id: identifiant,
      titre: z.string().nullable(),
      mots: compte,
    }),
  ),
  /** Les sections dont la double a deja un texte : elle reste intacte. */
  intactes: z.array(
    z.object({ numero: identifiant, double_id: identifiant, titre: z.string().nullable() }),
  ),
  /** Les sections sans double du meme numero, ou sans texte. */
  ignorees: z.array(
    z.object({ numero: identifiant, raison: z.enum(["pas_de_double", "sans_texte"]) }),
  ),
  applique: z.boolean(),
});
export type ApercuManuscrit = z.infer<typeof apercuManuscritSchema>;

// ---------------------------------------------------------------------------------------
// Les ecritures de Claude (le PC, `HISTOIRES_TOKEN`, tout en POST sous `/machine/`).

/**
 * Le texte que Claude ecrit dans une double. Jamais vide : un texte sans caractere ecrit (des
 * blancs ou des invisibles seulement) viderait la double sans la rendre a Nicolas, et seul
 * « Repartir de zero », par Nicolas, la vide.
 */
export const ecrireTexteMachineSchema = z.object({
  mode: z.enum(["propose", "rewrite"]),
  texte: z
    .string()
    .max(TEXTE_MAX)
    .refine(
      estEcrite,
      "un texte vide ne s ecrit pas : vider une double, c est Repartir de zero, par Nicolas",
    ),
  version: versionTexte,
});

export const compositionSchema = z
  .object({
    composition: z.string().max(4000).nullable(),
    gabarit: gabaritSchema.nullable(),
    personnages: z.array(identifiant).max(20),
    figurants: z.string().max(2000).nullable(),
  })
  .partial()
  .refine((c) => Object.keys(c).length > 0, "au moins un champ");

export const personnagesMachineSchema = z.object({
  personnages: z.array(personnageEcritSchema).min(1).max(30),
});

/**
 * Une double du plan : son type, ses pages, son titre et son intention, rien d'autre. Stricte :
 * un plan qui porte un texte ou une provenance est refuse (400), au lieu d'etre efface sans
 * bruit. Aucune route du PC ne pose un texte, pas meme par le plan.
 */
export const doublePlanSchema = z.strictObject({
  type: typeDoubleSchema,
  pages: z.number().int().min(0).max(2),
  titre: z.string().trim().max(120).nullable(),
  intention: z.string().max(2000).nullable(),
});
export type DoublePlan = z.infer<typeof doublePlanSchema>;

/** Le plan d'un livre : ses doubles dans l'ordre, la couverture d'abord (rang 0). */
export const planSchema = z.object({ doubles: z.array(doublePlanSchema).min(1).max(60) });
export type Plan = z.infer<typeof planSchema>;

export const kdpSchema = z
  .object({
    description: z.string().max(4000).nullable(),
    mots_cles: z.array(z.string().trim().min(1).max(50)).max(7),
    categories: z.array(z.string().trim().min(1).max(200)).max(3),
  })
  .partial()
  .refine((k) => Object.keys(k).length > 0, "au moins un champ");

/** `lire` : tout ce que Claude doit savoir d'un livre avant d'y ecrire. */
export const livreCompletSchema = z.object({
  livre: livreSchema,
  personnages: z.array(personnageSchema),
  doubles: z.array(doubleSchema),
  declaration_texte: declarationTexteSchema,
});
export type LivreComplet = z.infer<typeof livreCompletSchema>;

/**
 * Le plan d'un livre neuf (spec, section 2) : couverture, titre, liminaire, quatorze doubles
 * `histoire` et la note pour les parents, soit 32 pages. Un livre recoit un autre plan par
 * `plan` (l'album du deuil en a seize, amendement 1).
 */
export const PLAN_PAR_DEFAUT: readonly DoublePlan[] = [
  { type: "couverture", pages: 0, titre: "Couverture", intention: null },
  { type: "titre", pages: 1, titre: "Page de titre", intention: null },
  {
    type: "liminaire",
    pages: 2,
    titre: "Liminaire",
    intention: "Copyright, mention IA, dédicace.",
  },
  ...Array.from({ length: 14 }, () => ({
    type: "histoire" as const,
    pages: 2,
    titre: null,
    intention: null,
  })),
  { type: "note", pages: 1, titre: "Pour les parents", intention: null },
];
